import {
  contrastRatio,
  oklchToRgb,
  parseHex,
  rgbToOklch,
  toChannels,
  type Oklch,
  type Rgb,
} from "./color";
import type { ThemeToken } from "./defaults";

export type ThemeInput = {
  backgroundColor: string;
  primaryColor: string;
  accentColor: string;
};

export type DerivedTheme = {
  channels: Record<ThemeToken, string>;
  scrimAlpha: string;
  isDark: boolean;
};

const WHITE: Rgb = { r: 255, g: 255, b: 255 };
const BLACK: Rgb = { r: 0, g: 0, b: 0 };
const TEXT_CONTRAST = 4.5;
const L_STEP = 0.01;

const STATUS = {
  success: "#036346",
  error: "#B91C1C",
  warning: "#92400E",
} as const;
const WARNING_SOFT_LIGHT = "#FEF3C7";

/** Lightness steps away from the page, measured from today's palette; doubled on dark pages. */
const SURFACE_STEPS = {
  card: -0.015,
  cardSecondary: 0.01,
  hover: 0.02,
  divider: 0.04,
  border: 0.05,
};
/** How far each text tone sits from the page towards ink; equals L 0.28/0.55/0.71 on today's page. */
const INK_SHARE = { tertiary: 0.716, secondary: 0.442, disabled: 0.279 };
const SOFT_LIGHTNESS = { light: 0.95, dark: 0.3 };
const SOFT_CHROMA_SCALE = 0.5;
/** The soft button's tint over the page, at rest and on hover (`primary-soft` in tailwind.config.js). */
const SOFT_ALPHA = { rest: 0.1, hover: 0.2 };
const SOFT_HOVER = { light: -0.02, dark: 0.04, chromaScale: 1.35 };
const PRIMARY_HOVER = {
  lighten: 0.08,
  minDarkHoverL: 0.27,
  darkBelow: 0.3,
  darken: -0.06,
};

/** `top` at `alpha` over `bottom`, as the browser composites a translucent fill. */
const over = (top: Rgb, bottom: Rgb, alpha: number): Rgb => ({
  r: Math.round(top.r * alpha + bottom.r * (1 - alpha)),
  g: Math.round(top.g * alpha + bottom.g * (1 - alpha)),
  b: Math.round(top.b * alpha + bottom.b * (1 - alpha)),
});

/** The text colour, white or black, that reads best on every given background. */
const bestTextOnAll = (backgrounds: Rgb[]): Rgb => {
  const worst = (text: Rgb) =>
    Math.min(...backgrounds.map((bg) => contrastRatio(text, bg)));
  return worst(WHITE) >= worst(BLACK) ? WHITE : BLACK;
};

const bestTextOn = (background: Rgb): Rgb =>
  contrastRatio(WHITE, background) >= contrastRatio(BLACK, background)
    ? WHITE
    : BLACK;

/** Moves lightness by `direction` until every pair reaches `min`, or lightness runs out. */
const pushLightness = (
  color: Oklch,
  against: Rgb[],
  min: number,
  direction: 1 | -1,
): Rgb => {
  let current = { ...color };
  let rgb = oklchToRgb(current);
  while (against.some((other) => contrastRatio(rgb, other) < min)) {
    if (current.l <= 0 || current.l >= 1) break;
    current = {
      ...current,
      l: Math.min(1, Math.max(0, current.l + direction * L_STEP)),
    };
    rgb = oklchToRgb(current);
  }
  return rgb;
};

const softOf = (base: Oklch, isDark: boolean): Oklch => ({
  l: isDark ? SOFT_LIGHTNESS.dark : SOFT_LIGHTNESS.light,
  c: base.c * SOFT_CHROMA_SCALE,
  h: base.h,
});

export const deriveTheme = (input: ThemeInput): DerivedTheme => {
  const pageRgb = parseHex(input.backgroundColor);
  const page = rgbToOklch(pageRgb);
  const pageContrast = bestTextOn(pageRgb);
  const isDark = pageContrast === WHITE;
  const towardInk: 1 | -1 = isDark ? 1 : -1;
  const scale = isDark ? 2 : 1;

  const surface = (step: number) => {
    const l = page.l + towardInk * step * scale;
    const ratio = page.l > 0 ? l / page.l : 1;
    return oklchToRgb({ l, c: page.c * ratio, h: page.h });
  };
  const surfaceCard = surface(SURFACE_STEPS.card);
  const surfaceCardSecondary = surface(SURFACE_STEPS.cardSecondary);
  const surfaceHover = surface(SURFACE_STEPS.hover);

  const primaryRgb = parseHex(input.primaryColor);
  const primary = rgbToOklch(primaryRgb);

  // Text takes the primary colour, pushed until it reads on every surface it is drawn on.
  const textSurfaces = [
    pageRgb,
    surfaceCard,
    surfaceCardSecondary,
    surfaceHover,
    over(pageContrast, pageRgb, SOFT_ALPHA.rest),
    over(pageContrast, pageRgb, SOFT_ALPHA.hover),
  ];
  const ink = pushLightness(primary, textSurfaces, TEXT_CONTRAST, towardInk);
  const inkColor = rgbToOklch(ink);
  const inkTone = (share: number): Oklch => ({
    l: page.l + (inkColor.l - page.l) * share,
    c: page.c + (inkColor.c - page.c) * share,
    h: inkColor.c >= page.c ? inkColor.h : page.h,
  });
  const inkSecondary = pushLightness(
    inkTone(INK_SHARE.secondary),
    [pageRgb],
    TEXT_CONTRAST,
    towardInk,
  );

  const primaryHoverL =
    primary.l < PRIMARY_HOVER.darkBelow
      ? Math.max(primary.l + PRIMARY_HOVER.lighten, PRIMARY_HOVER.minDarkHoverL)
      : primary.l + PRIMARY_HOVER.darken;
  const primaryHover = oklchToRgb({ ...primary, l: primaryHoverL });

  const accentInput = rgbToOklch(parseHex(input.accentColor));
  const accentSoft = softOf(accentInput, isDark);
  const accentSoftRgb = oklchToRgb(accentSoft);
  const accent = pushLightness(
    accentInput,
    [accentSoftRgb, surfaceCard],
    TEXT_CONTRAST,
    towardInk,
  );
  const softHoverStep = isDark ? SOFT_HOVER.dark : SOFT_HOVER.light;

  const status = (hex: string) =>
    pushLightness(
      rgbToOklch(parseHex(hex)),
      [pageRgb],
      TEXT_CONTRAST,
      towardInk,
    );
  const success = status(STATUS.success);
  const error = status(STATUS.error);
  const warning = status(STATUS.warning);
  const onStatus = [WHITE, BLACK].reduce((best, candidate) => {
    const worst = (c: Rgb) =>
      Math.min(...[success, error, warning].map((s) => contrastRatio(c, s)));
    return worst(candidate) > worst(best) ? candidate : best;
  });

  // Button text takes the secondary colour when it reads on the button at rest and on hover.
  const onPrimary = [primaryRgb, primaryHover].every(
    (button) => contrastRatio(pageRgb, button) >= TEXT_CONTRAST,
  )
    ? pageRgb
    : bestTextOnAll([primaryRgb, primaryHover]);

  const channels: Record<ThemeToken, string> = {
    "surface-page": toChannels(pageRgb),
    "surface-card": toChannels(surfaceCard),
    "surface-card-secondary": toChannels(surfaceCardSecondary),
    "surface-hover": toChannels(surfaceHover),
    divider: toChannels(surface(SURFACE_STEPS.divider)),
    border: toChannels(surface(SURFACE_STEPS.border)),
    ink: toChannels(ink),
    "ink-secondary": toChannels(inkSecondary),
    "ink-tertiary": toChannels(
      pushLightness(
        inkTone(INK_SHARE.tertiary),
        [pageRgb],
        TEXT_CONTRAST,
        towardInk,
      ),
    ),
    "ink-disabled": toChannels(oklchToRgb(inkTone(INK_SHARE.disabled))),
    primary: toChannels(primaryRgb),
    "primary-hover": toChannels(primaryHover),
    "primary-soft-base": toChannels(pageContrast),
    "on-primary": toChannels(onPrimary),
    // A themed site confirms in its primary accent; without a theme the toast stays green.
    "toast-success": toChannels(primaryRgb),
    "on-toast-success": toChannels(onPrimary),
    accent: toChannels(accent),
    "accent-soft": toChannels(accentSoftRgb),
    "accent-soft-hover": toChannels(
      oklchToRgb({
        l: accentSoft.l + softHoverStep,
        c: accentSoft.c * SOFT_HOVER.chromaScale,
        h: accentSoft.h,
      }),
    ),
    "on-accent": toChannels(bestTextOn(accent)),
    success: toChannels(success),
    error: toChannels(error),
    warning: toChannels(warning),
    "warning-soft": toChannels(
      isDark
        ? oklchToRgb(softOf(rgbToOklch(warning), isDark))
        : parseHex(WARNING_SOFT_LIGHT),
    ),
    "on-status": toChannels(onStatus),
  };

  return { channels, scrimAlpha: isDark ? "0.6" : "0.5", isDark };
};
